/**
 * Color-bucket classification for yarn library filtering.
 *
 * Veronika's request (2026-05-05): she wants to filter the My Yarns
 * page by color family — white / yellow / orange / red / green / blue /
 * brown / black, plus a "multicolor" toggle for variegated, gradient,
 * tweed, and dyed colorways that don't sit cleanly in one bucket.
 *
 * Resolution priority per yarn (mirrors Veronika's UX brief):
 *   1. `isMulticolor === true`            → multicolor bucket only
 *   2. `colorHex` set and parseable       → bucket via HSL
 *   3. `colorPhoto` present (no hex)      → sample dominant pixel bucket
 *   4. neither                            → 'unknown' (filter shows nothing)
 *
 * Grey became its own bucket on 2026-08-31, when Veronika added a yarn whose
 * colour is called "Grey" and had to file it under White: "We need to add grey
 * color". Before that, low-saturation colours were coerced into brown / white /
 * black by lightness, so a mid grey was called BROWN — which is the part that
 * reads as plainly wrong when you are looking at your stash by colour.
 */

export type ColorBucket =
  | 'white' | 'yellow' | 'orange' | 'red' | 'pink' | 'violet'
  | 'green' | 'blue' | 'brown' | 'grey' | 'black'
  | 'unknown';

export const COLOR_BUCKETS: ColorBucket[] = [
  'white', 'yellow', 'orange', 'red', 'pink', 'violet',
  'green', 'blue', 'brown', 'grey', 'black',
];

export interface BucketSwatch {
  bucket: ColorBucket;
  hex: string;
  labelKey:
    | 'colorBucketWhite' | 'colorBucketYellow' | 'colorBucketOrange'
    | 'colorBucketRed' | 'colorBucketPink' | 'colorBucketViolet'
    | 'colorBucketGreen' | 'colorBucketBlue' | 'colorBucketBrown'
    | 'colorBucketGrey' | 'colorBucketBlack';
}

export const BUCKET_SWATCHES: BucketSwatch[] = [
  { bucket: 'white', hex: '#F4F4F4', labelKey: 'colorBucketWhite' },
  { bucket: 'yellow', hex: '#FCC117', labelKey: 'colorBucketYellow' },
  { bucket: 'orange', hex: '#FB5E00', labelKey: 'colorBucketOrange' },
  { bucket: 'red', hex: '#E40000', labelKey: 'colorBucketRed' },
  { bucket: 'pink', hex: '#F584B5', labelKey: 'colorBucketPink' },
  { bucket: 'violet', hex: '#8E44AD', labelKey: 'colorBucketViolet' },
  { bucket: 'green', hex: '#3FA34D', labelKey: 'colorBucketGreen' },
  { bucket: 'blue', hex: '#2D7FF9', labelKey: 'colorBucketBlue' },
  { bucket: 'brown', hex: '#7A4A1F', labelKey: 'colorBucketBrown' },
  { bucket: 'grey', hex: '#8C8C8C', labelKey: 'colorBucketGrey' },
  { bucket: 'black', hex: '#1A1A1A', labelKey: 'colorBucketBlack' },
];

/** Representative hex per bucket — used as the swatch fallback when a
 *  yarn has no colorPhoto. Lets the rest of the UI keep reading
 *  `colorHex` while the user only ever picks a bucket. */
export function bucketRepresentativeHex(bucket: ColorBucket | 'multicolor'): string {
  if (bucket === 'multicolor') return '#999999';
  const swatch = BUCKET_SWATCHES.find(s => s.bucket === bucket);
  return swatch?.hex || '#D4A574';
}

function hexToRgb(hex: string): { r: number; g: number; b: number } | null {
  const m = /^#?([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(hex.trim());
  if (!m) return null;
  let h = m[1];
  if (h.length === 3) h = h.split('').map(c => c + c).join('');
  const n = parseInt(h, 16);
  return { r: (n >> 16) & 0xff, g: (n >> 8) & 0xff, b: n & 0xff };
}

function rgbToHsl(r: number, g: number, b: number): { h: number; s: number; l: number } {
  const rn = r / 255, gn = g / 255, bn = b / 255;
  const max = Math.max(rn, gn, bn);
  const min = Math.min(rn, gn, bn);
  const l = (max + min) / 2;
  let h = 0; let s = 0;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    if (max === rn) h = ((gn - bn) / d + (gn < bn ? 6 : 0));
    else if (max === gn) h = ((bn - rn) / d + 2);
    else h = ((rn - gn) / d + 4);
    h *= 60;
  }
  return { h, s, l };
}

export function bucketForHex(hex: string | undefined | null): ColorBucket {
  if (!hex) return 'unknown';
  const rgb = hexToRgb(hex);
  if (!rgb) return 'unknown';
  const { h, s, l } = rgbToHsl(rgb.r, rgb.g, rgb.b);

  // Achromatic territory: very dark, very light, or near-zero saturation.
  if (l < 0.10) return 'black';
  if (l > 0.90 && s < 0.20) return 'white';
  if (s < 0.12) {
    // Low-saturation greys. Only the very ends belong to black and white; the
    // broad middle is grey in its own right, where it used to be called brown.
    if (l < 0.28) return 'black';
    if (l > 0.85) return 'white';
    return 'grey';
  }

  // Brown: warm hues (red→yellow) at low-mid lightness, modest saturation.
  // Anchored on the H/S/L profile of typical natural-fibre browns.
  if ((h < 50) && l < 0.45 && s < 0.75) return 'brown';
  if ((h < 35) && l < 0.55 && s < 0.55) return 'brown';

  // Hue wedges for saturated colors. Pink got its own bucket in
  // build 1041 (Veronika 2026-05-05) — soft warm pinks at low-mid
  // saturation get scooped before the red/violet split, then violet
  // covers 255-310°, pink 310-345°, red wraps 345-15°.
  if (h >= 310 && h < 345 && l >= 0.55) return 'pink';
  if (h < 15 || h >= 345) return 'red';
  // 44 rather than 45: the app's own yellow swatch (#FCC117) sits at 44.6 deg
  // and was classifying as ORANGE, so the colour shown in the picker disagreed
  // with the bucket a yarn landed in. Caught by round-tripping every swatch
  // through this function.
  if (h < 44) return 'orange';
  if (h < 70) return 'yellow';
  if (h < 170) return 'green';
  if (h < 255) return 'blue';
  if (h < 310) return 'violet';
  return 'pink';
}

/**
 * Determine the dominant color bucket of an image by histogramming
 * each non-transparent pixel into a per-bucket count and returning the
 * mode. Histogramming buckets directly (rather than averaging RGB)
 * avoids the classic "average of red+blue = gray" trap that makes
 * heathered or marled yarns disappear into the brown bucket.
 *
 * Caller passes a loaded HTMLImageElement; we resample to 24×24 on a
 * scratch canvas, so cost is constant regardless of source resolution.
 */
export function bucketForImage(img: HTMLImageElement): ColorBucket | null {
  try {
    const SIZE = 24;
    const c = document.createElement('canvas');
    c.width = SIZE; c.height = SIZE;
    const ctx = c.getContext('2d', { willReadFrequently: true });
    if (!ctx) return null;
    ctx.drawImage(img, 0, 0, SIZE, SIZE);
    const data = ctx.getImageData(0, 0, SIZE, SIZE).data;
    const counts: Record<string, number> = {};
    for (let i = 0; i < data.length; i += 4) {
      const a = data[i + 3];
      if (a < 128) continue;
      const r = data[i], g = data[i + 1], b = data[i + 2];
      const hex = '#' + [r, g, b].map(v => v.toString(16).padStart(2, '0')).join('');
      const bucket = bucketForHex(hex);
      counts[bucket] = (counts[bucket] || 0) + 1;
    }
    let best: ColorBucket | null = null;
    let bestCount = 0;
    for (const k of Object.keys(counts)) {
      if (counts[k] > bestCount) {
        best = k as ColorBucket;
        bestCount = counts[k];
      }
    }
    return best;
  } catch {
    return null;
  }
}
